import { PDFDocument, rgb, StandardFonts } from 'pdf-lib';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { getEmpresaAtual } from './api';

// Formata número como moeda BRL
const BRL = (v) =>
  (Number(v) || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

// Valor total de uma linha de serviço (recorrente multiplica pela duração em meses)
const valorLinha = (s) =>
  (Number(s.valor) || 0) *
  (Number(s.quantidade) || 0) *
  (s.tipo === 'recorrente' ? Number(s.duracao) || 1 : 1);

const generatePdfServicos = async (proposta) => {
  const pdfDoc = await PDFDocument.create();
  const regular = await pdfDoc.embedFont(StandardFonts.Helvetica);
  const bold = await pdfDoc.embedFont(StandardFonts.HelveticaBold);
  const italic = await pdfDoc.embedFont(StandardFonts.HelveticaOblique);

  // Logo da empresa configurada (antes tentava 4 nomes de arquivo em sequência)
  let logo = null;
  try {
    const empresa = await getEmpresaAtual();
    if (empresa?.logoPrincipal) {
      const base64 = empresa.logoPrincipal.split(',')[1];
      const bin = atob(base64);
      const bytes = new Uint8Array(bin.length);
      for (let i = 0; i < bin.length; i += 1) bytes[i] = bin.charCodeAt(i);
      logo = await pdfDoc.embedPng(bytes);
    }
  } catch (e) { logo = null; }

  const W = 595.28;
  const H = 841.89;
  const margin = 50;
  const contentW = W - margin * 2;
  const colors = {
    black: rgb(0, 0, 0),
    gray: rgb(0.45, 0.45, 0.45),
    line: rgb(0.6, 0.6, 0.6),
    headerBg: rgb(0.1, 0.1, 0.1),
    rowBg: rgb(0.92, 0.92, 0.92),
    white: rgb(1, 1, 1),
  };

  let page;
  let y; // cursor: topo da próxima linha a desenhar

  // sanitiza para o encoding WinAnsi do pdf-lib (evita exceções com caracteres fora do conjunto)
  const san = (t) => String(t == null ? '' : t).replace(/\u00A0/g, ' ');

  const drawHeader = () => {
    const top = H - margin;
    let textX = margin;
    if (logo) {
      const scale = 90 / logo.width;
      const lw = logo.width * scale;
      const lh = logo.height * scale;
      page.drawImage(logo, { x: margin, y: top - lh + 6, width: lw, height: lh });
      textX = margin + lw + 20;
    }
    page.drawText('MARCKI Engenharia', { x: textX, y: top - 8, font: bold, size: 11, color: colors.black });
    page.drawText(san('R. Afonso Pena nº 594, Sala 1, Centro, Araguari-MG'), { x: textX, y: top - 22, font: bold, size: 8, color: colors.black });
    page.drawText('(34) 98887-5093/ (34) 98854-1334', { x: textX, y: top - 35, font: regular, size: 8, color: colors.black });
    page.drawText('novackiandrade.engenharia@gmail.com', { x: textX, y: top - 46, font: regular, size: 8, color: colors.black });
  };

  const newPage = () => {
    page = pdfDoc.addPage([W, H]);
    drawHeader();
    y = H - margin - 70;
  };

  const wrapText = (text, font, size, maxW) => {
    const out = [];
    const rawLines = san(text).split('\n');
    for (let ri = 0; ri < rawLines.length; ri += 1) {
      const words = rawLines[ri].split(' ');
      let line = '';
      for (let wi = 0; wi < words.length; wi += 1) {
        const w = words[wi];
        const test = line ? `${line} ${w}` : w;
        if (font.widthOfTextAtSize(test, size) > maxW && line) {
          out.push(line);
          line = w;
        } else {
          line = test;
        }
      }
      out.push(line);
    }
    return out;
  };

  const ensure = (need) => {
    if (y - need < margin + 10) newPage();
  };

  const par = (text, opts = {}) => {
    const {
      font = regular, size = 10, lineHeight = 14, color = colors.black,
      x = margin, maxW = contentW, gapAfter = 6, align = 'left',
    } = opts;
    const linhasPar = wrapText(text, font, size, maxW);
    for (let pi = 0; pi < linhasPar.length; pi += 1) {
      const ln = linhasPar[pi];
      ensure(lineHeight);
      let lx = x;
      if (align === 'center') lx = (W - font.widthOfTextAtSize(ln, size)) / 2;
      page.drawText(ln, { x: lx, y: y - size, font, size, color });
      y -= lineHeight;
    }
    y -= gapAfter;
  };

  const labelVal = (label, value) => {
    ensure(18);
    page.drawText(san(label), { x: margin, y: y - 11, font: bold, size: 11, color: colors.black });
    const lw = bold.widthOfTextAtSize(`${label} `, 11);
    page.drawText(san(value), { x: margin + lw, y: y - 11, font: regular, size: 11, color: colors.black });
    y -= 18;
  };

  // ── Tabela ──
  const cols = [
    { w: contentW * 0.42 },
    { w: contentW * 0.20 },
    { w: contentW * 0.16 },
    { w: contentW * 0.22 },
  ];
  const colX = [];
  {
    let cx = margin;
    for (let ci = 0; ci < cols.length; ci += 1) { colX.push(cx); cx += cols[ci].w; }
  }

  const tableRow = (cells, { negrito = false, bg = null } = {}) => {
    const f = negrito ? bold : regular;
    const size = 9;
    const lineH = size + 4;
    const cellLines = cells.map((txt, i) => wrapText(txt, f, size, cols[i].w - 8));
    const rowLines = Math.max(...cellLines.map((l) => l.length), 1);
    const rowH = rowLines * lineH + 6;
    ensure(rowH);
    const top = y;
    const bottom = y - rowH;
    if (bg) page.drawRectangle({ x: margin, y: bottom, width: contentW, height: rowH, color: bg });
    page.drawRectangle({ x: margin, y: bottom, width: contentW, height: rowH, borderColor: colors.line, borderWidth: 0.5 });
    for (let i = 1; i < cols.length; i += 1) {
      page.drawLine({ start: { x: colX[i], y: bottom }, end: { x: colX[i], y: top }, thickness: 0.5, color: colors.line });
    }
    cells.forEach((txt, i) => {
      let ty = top - size - 4;
      const linhasCel = cellLines[i];
      for (let ci = 0; ci < linhasCel.length; ci += 1) {
        page.drawText(linhasCel[ci], { x: colX[i] + 4, y: ty, font: f, size, color: colors.black });
        ty -= lineH;
      }
    });
    y = bottom;
  };

  // ════════ CONTEÚDO ════════
  newPage();

  par('Proposta comercial de Serviços', { font: bold, size: 18, lineHeight: 22, align: 'center', gapAfter: 16 });

  labelVal('Cliente:', proposta.cliente?.nome);
  labelVal('Cidade:', proposta.localinstalacao?.cidade?.nome);
  labelVal('Estado:', proposta.localinstalacao?.estado?.sigla || proposta.localinstalacao?.estado?.nome);
  y -= 8;

  // Barra título da tabela
  ensure(22);
  page.drawRectangle({ x: margin, y: y - 18, width: contentW, height: 18, color: colors.headerBg });
  const tituloTabela = 'Detalhamento de Serviços';
  page.drawText(san(tituloTabela), { x: margin + (contentW - bold.widthOfTextAtSize(san(tituloTabela), 11)) / 2, y: y - 13, font: bold, size: 11, color: colors.white });
  y -= 18;

  // Cabeçalho da tabela
  tableRow(['Serviço', 'Valor Unit.', 'Qtde', 'Valor Total'], { bg: colors.rowBg, negrito: true });

  // Subtotal dos serviços e fator do acréscimo.
  // O acréscimo é uma margem de negociação: em vez de uma linha à parte, ele é
  // embutido proporcionalmente no valor de cada serviço (valor do sistema + acréscimo),
  // mantendo a coerência valor unitário × quantidade = valor total.
  const subTotalServicos = (proposta.servicos || []).reduce((acc, s) => acc + valorLinha(s), 0);
  const acrescimoServicos = Number(proposta.valorAcrescimoServicos) || 0;
  const fatorAcrescimo = subTotalServicos > 0 && acrescimoServicos > 0
    ? (subTotalServicos + acrescimoServicos) / subTotalServicos
    : 1;

  // Linhas de serviços (com acréscimo já embutido no valor unitário e no total)
  (proposta.servicos || []).forEach((s) => {
    const qtd = `${s.quantidade}${s.unidade ? ` ${s.unidade}` : ''}${s.tipo === 'recorrente' ? ` x ${s.duracao || 1} mes(es)` : ''}`;
    const valorUnit = (Number(s.valor) || 0) * fatorAcrescimo;
    const valorTotalLinha = valorLinha(s) * fatorAcrescimo;
    tableRow([s.nome, BRL(valorUnit), qtd, BRL(valorTotalLinha)]);
  });

  // Deslocamento
  if (Number(proposta.valorDeslocamento) > 0) {
    tableRow(['Deslocamento', BRL(2), `${proposta.kmrodado || ''}`, BRL(proposta.valorDeslocamento)]);
  }
  // Indicação
  if (Number(proposta.valorIndicacao) > 0) {
    tableRow(['Indicação', '', '', BRL(proposta.valorIndicacao)]);
  }
  // Desconto
  if (Number(proposta.valorDescontoServicos) > 0) {
    tableRow(['Desconto', '', '', `- ${BRL(proposta.valorDescontoServicos)}`]);
  }
  // Total
  tableRow(['Total', '', '', BRL(proposta.valor)], { bg: colors.rowBg, negrito: true });

  y -= 16;

  // Observações
  par('Observações:', { font: bold, size: 11, gapAfter: 4 });
  par('1. Todos os projetos acompanham suas respectivas listas de materiais', { size: 10, x: margin + 14, maxW: contentW - 14, gapAfter: 2 });
  par('2. Todos os projetos acompanham a respectiva ART', { size: 10, x: margin + 14, maxW: contentW - 14, gapAfter: 12 });

  // Forma de Pagamento
  par('Forma de Pagamento:', { font: bold, size: 11, gapAfter: 4 });
  const formaPagamento = proposta.formapagamento?.nome
    ? `${proposta.formapagamento.nome}${proposta.parcelas ? ` em ${proposta.parcelas}x` : ''}`
    : '50% (cinquenta por cento) no aceite da proposta e 50% (cinquenta por cento) na conclusão do serviço';
  par(formaPagamento, { size: 10, gapAfter: 14 });

  // Termo de Condições Gerais e Contratuais
  par('TERMO DE CONDIÇÕES GERAIS E CONTRATUAIS', { font: bold, size: 11, align: 'center', gapAfter: 8 });
  par('As partes, ao firmarem o presente orçamento, concordam que este instrumento passa a ter natureza contratual, vinculando-se às seguintes condições:', { size: 10, gapAfter: 8 });

  const clausulas = [
    ['Cláusula 1ª - Natureza contratual e força executiva', '1.1 - O presente orçamento, após devidamente aceito e assinado pelas partes, constitui instrumento contratual válido e eficaz, obrigando as partes ao seu fiel cumprimento, sendo considerado título executivo extrajudicial, nos termos do art. 784, inciso III, do Código de Processo Civil.'],
    ['Cláusula 2ª - Forma de pagamento', '2.1 - O pagamento será realizado conforme descrito neste orçamento, sendo condição para início dos serviços o adimplemento da parcela inicial. Para os serviços que serão considerados como repetição (do segundo equipamento em diante) a solicitação será feita de forma individual, sem quantidade mínima e sem quantidade máxima. Para cada serviço solicitado será gerado um boleto bancário individual.'],
    ['Cláusula 3ª - Inadimplemento e mora', '3.1 - O atraso no pagamento de qualquer parcela implicará a constituição automática em mora, independentemente de notificação, sujeitando o contratante ao pagamento de multa de 10% sobre o valor em aberto, juros de mora de 1% ao mês e correção monetária pelo índice IPCA.'],
    ['Cláusula 4ª - Protesto e negativação', '4.1 - O atraso superior a 30 (trinta) dias autoriza a contratada, independentemente de aviso prévio, a promover o protesto do presente instrumento, bem como proceder à inscrição do nome do contratante nos órgãos de proteção ao crédito, como medida legítima de resguardo de seu direito creditório.'],
    ['Cláusula 5ª - Suspensão dos serviços', '5.1 - Em caso de inadimplemento, fica facultado à contratada suspender a execução dos serviços até a regularização dos pagamentos, sem que isso gere qualquer penalidade ou responsabilidade.'],
    ['Cláusula 6ª - Honorários e cobrança', '6.1 - Na hipótese de cobrança judicial ou extrajudicial, serão devidos honorários advocatícios no percentual de 10% sobre o valor do débito, além das demais despesas incorridas.'],
    ['Cláusula 7ª - Escopo dos serviços', '7.1 - Os serviços objeto do presente instrumento limitam-se estritamente ao descritivo constante neste orçamento, não estando incluídas revisões adicionais, alterações de projeto ou serviços extras, que deverão ser previamente ajustados entre as partes.'],
    ['Cláusula 8ª - Responsabilidade técnica', '8.1 - A responsabilidade da contratada restringe-se à elaboração dos projetos, não abrangendo a execução da obra, que deverá seguir rigorosamente as especificações técnicas fornecidas.'],
    ['Cláusula 9ª - Natureza da obrigação', '9.1 - Os serviços objeto do presente instrumento constituem obrigação de meio, comprometendo-se a contratada a empregar diligência técnica e boas práticas profissionais, sem garantia de resultado ou aprovação por terceiros, não se caracterizando a presente contratação como relação de consumo, razão pela qual não se aplicam as disposições da Lei nº 8.078/1990 (Código de Defesa do Consumidor).'],
    ['Cláusula 10ª - Assinatura eletrônica', '10.1 - As partes convencionam que o presente instrumento poderá ser firmado por meio de assinatura eletrônica, inclusive mediante utilização de plataforma digital, hipótese em que reconhecem, de forma irrevogável e irretratável a autenticidade, integridade, validade jurídica e eficácia do presente contrato. Em caso de assinatura física, o presente instrumento será assinado por duas testemunhas.'],
    ['Cláusula 11ª - Foro', '11.1 - Fica eleito o foro da comarca de Araguari/MG para dirimir quaisquer controvérsias oriundas deste instrumento, com renúncia a qualquer outro, por mais privilegiado que seja.'],
  ];
  clausulas.forEach(([titulo, corpo]) => {
    par(titulo, { font: bold, size: 10, lineHeight: 13, gapAfter: 2 });
    par(corpo, { size: 10, lineHeight: 13, gapAfter: 8 });
  });

  // Data
  const dataProposta = proposta.data ? new Date(proposta.data) : new Date();
  par(format(dataProposta, "'Araguari,' dd 'de' MMMM 'de' yyyy", { locale: ptBR }), { size: 10, gapAfter: 20 });

  // Assinaturas
  const assinaturaLinha = () => {
    ensure(30);
    page.drawLine({ start: { x: margin, y: y - 6 }, end: { x: margin + 300, y: y - 6 }, thickness: 0.7, color: colors.black });
    y -= 16;
  };

  par('CONTRATADA:', { size: 10, gapAfter: 24 });
  assinaturaLinha();
  par('Novacki & Andrade Engenharia LTDA', { font: italic, size: 10, gapAfter: 2 });
  par('Eng. Me. Mateus Andrade Marques de Lima - CREA MG 200.491/D', { font: italic, size: 10, gapAfter: 2 });
  par('Engª Me. Débora Falcomer Novacki - CREA/MG: 200.490/D', { font: italic, size: 10, gapAfter: 22 });

  par('CONTRATANTE:', { size: 10, gapAfter: 24 });
  assinaturaLinha();
  par(proposta.cliente?.nome || '', { font: italic, size: 10, gapAfter: 2 });
  if (proposta.cliente?.cpfcnpj || proposta.cliente?.cpf || proposta.cliente?.cnpj) {
    par(`CPF/CNPJ: ${proposta.cliente.cpfcnpj || proposta.cliente.cpf || proposta.cliente.cnpj}`, { font: italic, size: 10, gapAfter: 2 });
  }

  // ── Salvar e baixar ──
  const pdfBytes = await pdfDoc.save();
  const blob = new Blob([pdfBytes], { type: 'application/pdf' });
  const link = document.createElement('a');
  link.href = window.URL.createObjectURL(blob);
  const nomeArquivo = (proposta.cliente?.nome || 'cliente').replace(/\s+/g, '_');
  link.download = `proposta_servicos_${nomeArquivo}.pdf`;
  link.click();
  window.URL.revokeObjectURL(link.href);
};

export default generatePdfServicos;